import { Button, Descriptions, Divider, Flex, Input, List, Modal, Spin } from "antd"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import isEqual from "lodash/isEqual"
import { DeviceContext, DeviceRequestType, UpdateDeviceContext } from "../../../../context/deviceContext"
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext"
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper"

const defaultDeviceInfo: DeviceRequestType = {
  company_id: '',
  location: '',
  name: '',
}
export function CompanyDeviceManage() {
  const { registerDevice, updateDevice, regenerateDeviceKey } = useContext(UpdateDeviceContext)
  const { device } = useContext(DeviceContext)
  const { refreshDevice } = useContext(UpdateDeviceContext)
  const { company } = useContext(CompanyContext)
  const { refreshCompany } = useContext(UpdateCompanyContext)
  const { companyId, deviceId } = useParams()
  const navigate = useNavigate()

  const [deviceInfo, setDeviceInfo] = useState<DeviceRequestType>(defaultDeviceInfo)
  const [originalDeviceInfo, setOriginalDeviceInfo] = useState<DeviceRequestType>(defaultDeviceInfo)
  const [errors, setErros] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadDeviceManage = async () => {
      setIsPageReady(false)

      if (!companyId || !deviceId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      const isRegisterMode = deviceId === 'register'

      try {
        const [companyResult, deviceResult] = await Promise.all([
          refreshCompany(companyId),
          isRegisterMode
            ? Promise.resolve({ status: 200, data: null })
            : refreshDevice(deviceId),
        ])

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return
        }

        if (!isRegisterMode) {
          if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
            return
          }

          const deviceData = deviceResult.data
          if (!deviceData) {
            navigate(PATH_INFO.ERROR.PATH, { replace: true })
            return
          }

          const deviceCompanyId = deviceData.company?.id
          if (deviceCompanyId !== Number(companyId)) {
            navigate(PATH_INFO.ERROR.PATH, { replace: true })
            return
          }
        }

        if (isRegisterMode) {
          const initialData = { ...defaultDeviceInfo, company_id: companyId }
          setDeviceInfo(initialData)
          setOriginalDeviceInfo(initialData)
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating company device manage.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadDeviceManage()

    return () => {
      isCancelled = true
    }
  }, [companyId, deviceId, navigate, refreshCompany, refreshDevice])

  useEffect(() => {
    if (!isPageReady || deviceId === 'register') {
      return
    }

    const initialData = {
      company_id: companyId || '',
      location: device.location,
      name: device.name
    }

    setDeviceInfo(initialData)
    setOriginalDeviceInfo(initialData)
  }, [companyId, device, deviceId, isPageReady])

  const dataSource = useMemo(() => {
    const commonData = [
      {
        key: 'companyName',
        label: '企業',
        value: company?.company_name,
        type: 'text',
        disabled: true
      },
      {
        key: 'location',
        label: '拠点',
        value: deviceInfo.location,
        type: 'text',
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, location: el.target.value })) }
      },
      {
        key: 'name',
        label: 'デバイス名',
        value: deviceInfo.name,
        type: 'text',
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, name: el.target.value })) }
      },
    ]

    if (deviceId !== 'register') {
      commonData.push({
        key: 'api',
        label: 'API Key',
        value: device.api_key,
        type: 'text',
        disabled: true,
      })
    }

    return commonData
  }, [deviceInfo, device, deviceId, company])

  // デバイス登録/編集
  const onClickRegister = useCallback(async () => {
    if (!deviceId) return

    const newErrors: Record<string, boolean> = {};

    ['location', 'name'].forEach((key) => {
      if (deviceInfo[key as keyof DeviceRequestType] === '') {
        newErrors[key] = true
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErros(newErrors)
      Modal.warning({ content: '拠点名とデバイス名は必須です。', centered: true })
      return
    }

    setErros({})

    setIsLoading(true)
    try {
      if (deviceId === 'register') {
        const res = await registerDevice(deviceInfo)
        if (res) {
          Modal.success({ content: 'デバイスを新規登録しました！', centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: 'デバイスの登録に失敗しました。', centered: true })
        }
      } else {
        const res = await updateDevice({ location: deviceInfo.location, name: deviceInfo.name, device_id: deviceId })
        if (res) {
          Modal.success({ content: 'デバイスの更新が完了しました！', centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: 'デバイスの更新に失敗しました。', centered: true })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [deviceInfo, registerDevice, updateDevice, deviceId, navigate])

  const onClickBack = useCallback(() => { navigate(-1) }, [navigate])

  const handleRegenerate = useCallback(async () => {
    if (!companyId || !deviceId || deviceId === 'register') return

    const hasUnsavedChanges = !isEqual(originalDeviceInfo, deviceInfo)

    if (!hasUnsavedChanges) {
      Modal.warning({
        title: 'API Keyを再発行すると現在のAPI Keyは直ちに使用できなくなります。',
        content: '再発行しますか？',
        okType: 'danger',
        okCancel: true,
        centered: true,
        async onOk() {
          setIsLoading(true)
          try {
            const regenerateResult = await regenerateDeviceKey(deviceId)
            if (!regenerateResult) {
              Modal.error({ content: "API Key の再発行に失敗しました。", centered: true })
              return
            }

            const deviceResult = await refreshDevice(deviceId)
            if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
              return
            }

            Modal.success({ content: 'API Key を再発行しました！', centered: true, onOk() { navigate(-1) } })
          } catch {
            Modal.error({ content: "API Key の再発行に失敗しました。", centered: true })
          } finally {
            setIsLoading(false)
          }
        }
      })
      return
    }

    Modal.warning({
      title: '未保存の変更があります。',
      content: '変更を保存してAPI Keyを再発行しますか？',
      okCancel: true,
      centered: true,
      async onOk() {
        setIsLoading(true)
        try {
          const updateResult = await updateDevice({ location: deviceInfo.location, name: deviceInfo.name, device_id: deviceId })
          if (!updateResult) {
            Modal.error({ content: 'デバイス情報の更新に失敗しました。', centered: true })
            return
          }

          const regenerateResult = await regenerateDeviceKey(deviceId)
          if (!regenerateResult) {
            Modal.error({ content: "API Key の再発行に失敗しました。", centered: true })
            return
          }

          const deviceResult = await refreshDevice(deviceId)
          if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
            return
          }

          Modal.success({ content: 'デバイス情報を更新し、API Key を再発行しました。', centered: true, onOk() { navigate(-1) } })
        } catch (error) {
          Modal.error({ content: '処理中にエラーが発生しました。', centered: true })
        } finally {
          setIsLoading(false)
        }
      }
    })

  }, [companyId, deviceId, deviceInfo, originalDeviceInfo, refreshDevice, regenerateDeviceKey, navigate, updateDevice])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip="読み込み中..." />
      </Flex>
    )
  }

  return (
    <div>
      <Spin spinning={isLoading} fullscreen tip='処理中...' />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">デバイス{deviceId === 'register' ? '登録' : '編集'}画面</p>
      </div>
      <Divider />
      <Flex vertical align="center">
        <List
          header={<div className="h-4"></div>}
          bordered
          dataSource={dataSource}
          renderItem={(device) => (
            <List.Item style={{ background: '#FFFFFF', justifyContent: 'end' }} >
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={device.label} key={device.key} >
                  {device.key === 'api' ? (
                    <div className="flex space-x-2 w-[320px]">
                      <Input
                        disabled={device.disabled}
                        value={device.value}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleRegenerate}
                      >
                        再発行
                      </Button>
                    </div>
                  ) : (
                    <Input
                      disabled={device.disabled}
                      value={device.value}
                      onChange={device.onchange}
                      status={errors[device.key] ? 'error' : undefined}
                      className="w-[320px]"
                    />
                  )}
                </Descriptions.Item>
              </Descriptions>
            </List.Item>
          )}
          className="overflow-hidden bg-[#FAFAFA] text-center"
        />
        <Flex gap="large" className="mt-5">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack}>戻る</Button>
          <Button type="primary" size="large" onClick={onClickRegister}>{deviceId === 'register' ? '登録' : '更新'}</Button>
        </Flex>
      </Flex>
    </div>
  )
}
